import { inject, Injectable } from "@angular/core";
import _ from "lodash";
import { Observable, of, ReplaySubject } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import { SkogsvardAtgardsGrupp, SkogsvardAtgardsTyp } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CreateSkogsvardTradslagCommand } from "../commands/skogsvard-tradslag/CreateSkogsvardTradslagCommand";
import { DeleteSkogsvardTradslagCommand } from "../commands/skogsvard-tradslag/DeleteSkogsvardTradslagCommand";
import { UpdateSkogsvardTradslagCommand } from "../commands/skogsvard-tradslag/UpdateSkogsvardTradslagCommand";
import { Atgard } from "../models/atgard-models";
import { AtgardSkogsvardTradslag, VKAtgardSkogsvardTradslagDTO } from "../models/skogsvard/skogsvard-models";
import { AllSkogsvardTradslagService } from "./all-skogsvard-tradslag.service";
import { AtgardMediatorService } from "./atgard-mediator.service";
import { KontraktCommandService } from "./kontrakt-command.service";

@Injectable()
export class SkogsvardForyngringService {
    private readonly api = inject(ApiClientService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly allSkogsvardTradslagService = inject(AllSkogsvardTradslagService);

    private readonly skogsvardForyngringTradslagSrc = new ReplaySubject<AtgardSkogsvardTradslag[]>(1);
    private currentSkogsvardTradslagList: AtgardSkogsvardTradslag[] = [];
    private readonly skogsvardAtgardsTyperForTradslag = [
        SkogsvardAtgardsTyp.markberedningSadd,
        SkogsvardAtgardsTyp.foryngringSadd
    ];

    get skogsvardForyngringTradslag$() {
        return this.skogsvardForyngringTradslagSrc.asObservable();
    }

    constructor() {
        this.atgardMediatorService.atgard$
            .pipe(
                filter(x => this.skogsvardAtgardsTyperForTradslag.includes(x.atgardstyp)),
                switchMap(atgard => this.getForyngringTradslagForSkogsvard(atgard.id)))
            .subscribe(tradslag => {
                this.currentSkogsvardTradslagList = tradslag;
                this.publishChange();
            });
    }

    updateSkogsvardTradslag(tradslag: AtgardSkogsvardTradslag) {
        const atgardGuid = this.atgardMediatorService.atgard.guid;

        if (!this.currentSkogsvardTradslagList.find(x => x.guid === tradslag.guid)) {
            const createCmd = new CreateSkogsvardTradslagCommand(atgardGuid, tradslag.guid);
            this.kontraktCommandService.update(createCmd);
            this.currentSkogsvardTradslagList.push(tradslag);
        }

        const updateCmd = new UpdateSkogsvardTradslagCommand(atgardGuid, tradslag);
        this.kontraktCommandService.update(updateCmd);
        this.publishChange();

    }

    deleteSkogsvardTradslag(guid: string) {
        const atgardGuid = this.atgardMediatorService.atgard.guid;
        const cmd = new DeleteSkogsvardTradslagCommand(atgardGuid, guid);
        this.kontraktCommandService.update(cmd);

        this.currentSkogsvardTradslagList = this.currentSkogsvardTradslagList.filter(x => x.guid !== guid);
        this.publishChange();
    }

    getArealHa(tradslag: AtgardSkogsvardTradslag): number {
        return _.round(tradslag.arealandel / 100 * this.atgardMediatorService.atgard.nettoareal, 2);
    }

    getMalFrogiva(foryngringTradslag: AtgardSkogsvardTradslag[]): number {
        if (_.isNil(foryngringTradslag) || foryngringTradslag.length === 0) {
            return 0;
        }
        const malFrogiva = _.sumBy(foryngringTradslag, t => t.frogiva * this.getArealHa(t));
        const ha = _.sumBy(foryngringTradslag, t => this.getArealHa(t));
        return _.round(malFrogiva / ha, 2);
    }

    isSadd(skogsvardsAtgard: Atgard) {
        return this.isMarkberedningSadd(skogsvardsAtgard) || this.isForyngringSadd(skogsvardsAtgard);
    }

    isMarkberedningSadd(skogsvardsAtgard: Atgard): boolean {
        return skogsvardsAtgard.atgardsgrupp === SkogsvardAtgardsGrupp.markberedning &&
            skogsvardsAtgard.atgardstyp === SkogsvardAtgardsTyp.markberedningSadd;
    }

    isForyngringSadd(skogsvardsAtgard: Atgard): boolean {
        return skogsvardsAtgard.atgardsgrupp === SkogsvardAtgardsGrupp.foryngring &&
            skogsvardsAtgard.atgardstyp === SkogsvardAtgardsTyp.foryngringSadd;
    }

    private getForyngringTradslagForSkogsvard(atgardId?: number): Observable<VKAtgardSkogsvardTradslagDTO[]> {
        if (atgardId) {
            return this.api.get<VKAtgardSkogsvardTradslagDTO[]>(`virkeskop2/atgard/skogsvard/${atgardId}/foryngringtradslag`);
        } else { return of([]); }
    }

    private publishChange() {
        const listToPublish = [...this.currentSkogsvardTradslagList];
        this.skogsvardForyngringTradslagSrc.next(listToPublish);
        const tradslagsList = listToPublish.map(x => x.tradslagId);
        this.allSkogsvardTradslagService.updateCacheForAtgard(this.atgardMediatorService.atgard.guid, tradslagsList);
    }
}
